import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { offerings, positions } from "@/mocks/residual";
import { formatNumber, formatPct, formatUsd } from "@/lib/format";
export function SellPage({
  positionId,
  onBack,
  onListed
}: {
  positionId: string | null;
  onBack: () => void;
  onListed: () => void;
}) {
  const initialPositionId = positionId ?? positions[0]?.id ?? "";
  const [selectedId, setSelectedId] = useState(initialPositionId);
  const position = useMemo(() => positions.find(p => p.id === selectedId), [selectedId]);
  const offering = useMemo(() => offerings.find(o => o.id === position?.offeringId), [position]);
  const [tokens, setTokens] = useState<number>(position?.tokens ?? 0);
  const [askPrice, setAskPrice] = useState<number>(position && offering ? offering.tokenPrice : 0);
  if (!position || !offering) {
    return <div className="space-y-4">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="size-4" /> Back
				</Button>
				<p className="text-sm text-muted-foreground">
					You don't have any positions to sell yet.
				</p>
			</div>;
  }
  const total = tokens * askPrice;
  const platformFee = total * 0.01;
  const net = total - platformFee;
  const vsPrimary = (askPrice - offering.tokenPrice) / offering.tokenPrice * 100;
  return <div className="space-y-5">
			<Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
				<ArrowLeft className="size-4" /> Cancel
			</Button>

			<div>
				<h1 className="text-2xl font-semibold">List for sale</h1>
				<p className="text-sm text-muted-foreground">
					Tokens move into escrow when the listing is published. You can
					cancel any time before settlement.
				</p>
			</div>

			<Card>
				<CardContent className="space-y-4 pt-6">
					<div className="space-y-2">
						<Label>Position</Label>
						<select value={selectedId} onChange={e => {
            setSelectedId(e.target.value);
            const p_0 = positions.find(x => x.id === e.target.value);
            setTokens(p_0?.tokens ?? 0);
          }} className="block w-full rounded-md border bg-background px-3 py-2 text-sm">
							{positions.map(p_1 => {
              const o_0 = offerings.find(x_0 => x_0.id === p_1.offeringId);
              return <option key={p_1.id} value={p_1.id}>
										{o_0?.title ?? "Unknown"} — {formatNumber(p_1.tokens)}{" "}
										{o_0?.tokenSymbol}
									</option>;
            })}
						</select>
					</div>

					<div className="space-y-2">
						<Label htmlFor="tokens">Tokens to list</Label>
						<Input id="tokens" type="number" min={1} max={position.tokens} value={tokens} onChange={e_0 => setTokens(Math.min(position.tokens, Math.max(1, Number(e_0.target.value) || 0)))} />
						<div className="text-xs text-muted-foreground">
							You hold {formatNumber(position.tokens)}{" "}
							{offering.tokenSymbol}
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="price">Asking price per token (USD)</Label>
						<Input id="price" type="number" min={0} step="0.01" value={askPrice} onChange={e_1 => setAskPrice(Math.max(0, Number(e_1.target.value) || 0))} />
						<div className="text-xs text-muted-foreground">
							Primary sale price was {formatUsd(offering.tokenPrice)}.{" "}
							{askPrice > 0 && <span className={vsPrimary >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}>
									{vsPrimary > 0 ? "+" : ""}
									{formatPct(vsPrimary)} vs primary
								</span>}
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardContent className="space-y-2 pt-6">
					<Row label="Listing total" value={formatUsd(total)} />
					<Row label="Platform fee" value={formatUsd(platformFee)} hint="1% on settlement" />
					<Separator />
					<Row label="You receive on sale" value={formatUsd(net)} strong />
				</CardContent>
			</Card>

			<div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
				<Lock className="mr-1 inline size-3.5" />
				Tokens are locked in escrow as soon as the listing is published. They
				stay locked through negotiation and unlock back to your wallet if you
				cancel.
			</div>

			<Button size="lg" className="w-full" disabled={tokens <= 0 || askPrice <= 0} onClick={onListed}>
				Publish listing — escrow {formatNumber(tokens)} {offering.tokenSymbol}
			</Button>
		</div>;
}
function Row(t0) {
  const $ = _c(12);
  const {
    label,
    value,
    hint,
    strong
  } = t0;
  const t1 = strong ? "font-semibold" : "text-muted-foreground";
  let t2;
  if ($[0] !== hint) {
    t2 = hint && <span className="ml-1 text-xs text-muted-foreground">({hint})</span>;
    $[0] = hint;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label || $[3] !== t1 || $[4] !== t2) {
    t3 = <span className={t1}>{label}{t2}</span>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = strong ? "text-base font-semibold" : "font-medium tabular-nums";
  let t5;
  if ($[6] !== t4 || $[7] !== value) {
    t5 = <span className={t4}>{value}</span>;
    $[6] = t4;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="flex items-baseline justify-between text-sm">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}